import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  FulfillmentOrderResponseDto,
  OrderCancelRequestDto,
  OrderFulfillRequestDto,
  OrderResponseDto,
} from '@tiendanube/common';
import { Status as StatusEnum } from '@tiendanube/common/src/enums';
import { useAppDispatch } from 'App/store';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import useNuvemEnvioPrintLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import { cancelOrderById, getUpdateOrdersStatus } from '../../ordersSlice';
import {
  archiveOrder as archiveOrderById,
  cleanUpdateOrderStatus,
  fulfillOrderById,
  openOrderById,
  packOrderById,
  paidOrderById,
} from '../../ordersSlice/orderSlice';

function useQuickAction() {
  const dispatch = useAppDispatch();
  const status = useSelector(getUpdateOrdersStatus);

  const { printNuvemEnvioLabels } = useNuvemEnvioPrintLabel();

  const archiveOrder = useCallback(
    async (id: string) => {
      const payload = {
        isActionFromOrdersList: false,
      };
      await dispatch(archiveOrderById({ id, payload }));
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  const paidOrder = useCallback(
    async (id: string) => {
      await dispatch(paidOrderById({ id, isActionFromOrdersList: false }));
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  const openOrder = useCallback(
    async (id: string) => {
      await dispatch(openOrderById({ id, isActionFromOrdersList: false }));
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  const printNuvemEnvio = useCallback(
    async (
      externalSaleId: string,
      fulfillments: FulfillmentOrderResponseDto[],
      isMassivePrint = false,
      paymentStatus?: StatusEnum,
    ) => {
      printNuvemEnvioLabels(
        NE_PRINT_LABEL_SOURCE.ORDERS_LIST,
        fulfillments.map((fulfillment) => fulfillment.id),
        isMassivePrint,
        paymentStatus,
      );
    },
    [printNuvemEnvioLabels],
  );

  const packOrder = useCallback(
    async (orderId: string, fulfillments: FulfillmentOrderResponseDto[]) => {
      await dispatch(
        packOrderById({
          orderId,
          fulfillments:
            fulfillments?.length > 0
              ? fulfillments.map((ffo) => ({
                  fulfillmentId: ffo.id,
                  status: ffo.status,
                  shippingType: ffo.shipping_info.shipping_type,
                }))
              : [],
          isActionFromOrdersList: false,
        }),
      );
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  const fulfillOrder = useCallback(
    async (order: OrderResponseDto, action: OrderFulfillRequestDto) => {
      await dispatch(
        fulfillOrderById({
          orderId: order.id,
          action: {
            ...action,
            isActionFromOrdersList: false,
          },
        }),
      );
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  const cancelOrder = useCallback(
    async (id: string, action: OrderCancelRequestDto) => {
      action.isActionFromOrdersList = true;
      await dispatch(cancelOrderById({ id, action }));
      dispatch(cleanUpdateOrderStatus());
    },
    [dispatch],
  );

  return {
    archiveOrder,
    paidOrder,
    openOrder,
    packOrder,
    fulfillOrder,
    printNuvemEnvio,
    cancelOrder,
    status,
  };
}

export default useQuickAction;
